import {
  prepareImageData,
  runInspectionInWorker,
} from '../services/inspectClient';
import {
  ROI_WIDTH_MM,
  ROI_HEIGHT_MM,
  mmPerPixelAtDistance,
  roiAreaSqFt,
} from './distance';

export const ROI_CROP_FILL = 0.78;

function cropCenterRoi(imageData, fill = ROI_CROP_FILL) {
  const { width, height, data } = imageData;
  const roiW = Math.max(32, Math.round(width * fill));
  const roiH = Math.max(32, Math.round(height * fill));
  const x0 = Math.floor((width - roiW) / 2);
  const y0 = Math.floor((height - roiH) / 2);

  const canvas = document.createElement('canvas');
  canvas.width = roiW;
  canvas.height = roiH;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  const tmp = document.createElement('canvas');
  tmp.width = width;
  tmp.height = height;
  const tctx = tmp.getContext('2d');
  const full = tctx.createImageData(width, height);
  full.data.set(data);
  tctx.putImageData(full, 0, 0);
  ctx.drawImage(tmp, x0, y0, roiW, roiH, 0, 0, roiW, roiH);
  return {
    imageData: ctx.getImageData(0, 0, roiW, roiH),
    roiW,
    roiH,
    x0,
    y0,
    sourceW: width,
    sourceH: height,
  };
}

function toOverlaySpots(spots, crop, maxSpotDiameterMm) {
  const { roiW, roiH, x0, y0, sourceW, sourceH } = crop;
  return (spots || []).map((spot) => {
    const { minX, minY, maxX, maxY } = spot.bbox || {};
    // Normalize to FULL captured frame (0..1) so UI can map through object-fit:cover
    const nx = (x0 + minX) / sourceW;
    const ny = (y0 + minY) / sourceH;
    const nw = (maxX - minX + 1) / sourceW;
    const nh = (maxY - minY + 1) / sourceH;
    const diameterMm = Number(spot.diameterMm) || 0;
    const limit = maxSpotDiameterMm || 5;
    const oversized = diameterMm > limit;
    const isKnot = spot.type === 'wood_knot';
    const kind = isKnot ? 'ตาไม้' : 'จุดด่างดำ';
    const sizeText = oversized
      ? `Ø${diameterMm} มม. >${limit}`
      : `Ø${diameterMm} มม. ≤${limit}`;
    return {
      id: spot.id,
      type: spot.type || 'black_spot',
      diameterMm,
      nx,
      ny,
      nw,
      nh,
      // legacy % inside ROI (fallback)
      left: Math.max(0, (minX / roiW) * 100),
      top: Math.max(0, (minY / roiH) * 100),
      width: Math.max(0.5, ((maxX - minX + 1) / roiW) * 100),
      height: Math.max(0.5, ((maxY - minY + 1) / roiH) * 100),
      oversized,
      isKnot,
      soft: Boolean(spot.soft),
      label: `${kind} ${sizeText}`,
    };
  });
}

export async function runInspectionOnCanvas(canvas, usedDistanceCm, rules, options = {}) {
  const maxSide = options.maxSide ?? 960;
  const prepared = prepareImageData(canvas, maxSide);
  const mmPerPixel = mmPerPixelAtDistance(usedDistanceCm, prepared.width);
  const crop = cropCenterRoi(prepared.imageData, ROI_CROP_FILL);
  const sampleAreaSqFt = roiAreaSqFt(ROI_WIDTH_MM, ROI_HEIGHT_MM);

  const inspected = await runInspectionInWorker(crop.imageData, {
    mmPerPixel,
    maxSpotDiameterMm: rules.maxBlackSpotDiameterMm,
    largeSpotThresholdMm: rules.largeSpotThresholdMm,
    minDistanceBetweenLargeSpotsCm: rules.minDistanceBetweenLargeSpotsCm,
    maxBlackSpotsPerSqFt: rules.maxBlackSpotsPerSqFt,
    sampleAreaSqFt,
    nmsDistMm: 10,
    maxSpots: 60,
  });

  const overlaySpots = toOverlaySpots(
    inspected.spots,
    crop,
    rules.maxBlackSpotDiameterMm
  );

  return {
    ...inspected,
    sampleAreaSqFt,
    distanceCm: usedDistanceCm,
    overlaySpots,
  };
}
